import { Button, Field, Input } from '@ambernord/ui';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader.js';
import { acceptInvite, confirmPasswordReset } from '../lib/api.js';
import { playAudioCue } from '../lib/audio.js';

const MIN_PASSWORD_LENGTH = 12;

type Mode = 'invite' | 'reset';

/**
 * Viena lapa diviem "iestati paroli ar tokenu" ceļiem: `/accept-invite?token=` (uzaicinājums →
 * konts aktivizējas) un `/reset-password?token=` (aizmirsta parole). Bez auto-pieslēgšanās —
 * pēc panākuma saite uz /login.
 */
export default function SetPassword({ mode }: { mode: Mode }) {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const ns = mode === 'invite' ? 'acceptInvite' : 'resetPassword';
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = token && password.length >= MIN_PASSWORD_LENGTH && confirm === password;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setError(null);
    setSubmitting(true);
    try {
      const res =
        mode === 'invite'
          ? await acceptInvite(token, password)
          : await confirmPasswordReset(token, password);
      if (!res.ok || !res.data.ok) {
        const code = res.data.error;
        setError(
          code === 'invalid_or_expired'
            ? t(`${ns}.invalidToken`)
            : code === 'invalid_input'
              ? t('setPassword.tooShort', { min: MIN_PASSWORD_LENGTH })
              : t('status.error'),
        );
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      setDone(true);
    } catch {
      setError(t('status.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <AppHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10">
        <section className="rounded-lg border border-slate-200 p-6 dark:border-slate-800">
          <h2 className="mb-1 text-base font-medium">{t(`${ns}.heading`)}</h2>
          {done ? (
            <div data-testid="set-password-done">
              <p className="text-sm text-slate-700 dark:text-slate-300">{t(`${ns}.done`)}</p>
              <p className="mt-4 text-sm">
                <Link className="text-brand-600 hover:underline dark:text-brand-400" to="/login">
                  {t('setPassword.goToSignIn')}
                </Link>
              </p>
            </div>
          ) : !token ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {t(`${ns}.invalidToken`)}
            </p>
          ) : (
            <>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                {t(`${ns}.description`)}
              </p>
              <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
                <Field
                  label={t('setPassword.password')}
                  htmlFor="new-password"
                  hint={t('setPassword.hint', { min: MIN_PASSWORD_LENGTH })}
                  error={tooShort ? t('setPassword.tooShort', { min: MIN_PASSWORD_LENGTH }) : null}
                >
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
                <Field
                  label={t('setPassword.confirm')}
                  htmlFor="confirm-password"
                  error={mismatch ? t('setPassword.mismatch') : null}
                >
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </Field>
                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={submitting || !ready}>
                  {submitting ? t('status.loading') : t(`${ns}.submit`)}
                </Button>
              </form>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
